import React from 'react';
import { Search, Eye, FileText } from 'lucide-react';
import { QualityBadge, DocumentStatusBadge } from '../components/shared/StatusBadge';
import { useApp } from '../context/AppContext';
import { qualityRatingDescriptions } from '../data/mock';
import type { QualityRating } from '../data/mock';

export const DocumentsList: React.FC = () => {
  const { filteredDocuments, filters, setFilters, navigateToDocument } = useApp();

  // Sort by date newest first
  const sortedDocs = [...filteredDocuments].sort(
    (a, b) => new Date(b.uploadDate).getTime() - new Date(a.uploadDate).getTime()
  );

  return (
    <div className="space-y-6">
      {/* Filter Bar */}
      <div className="glass-card p-4 animate-slide-up">
        <div className="flex items-center gap-4">
          {/* Search */}
          <div className="relative flex-1">
            <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-surface-400" />
            <input
              type="text"
              placeholder="Search by file name, document name..."
              value={filters.search}
              onChange={(e) => setFilters({ search: e.target.value })}
              className="pl-9 pr-4 py-2.5 text-sm bg-surface-50 border border-surface-200 rounded-xl w-full focus:outline-none focus:ring-2 focus:ring-brand-400/20 focus:border-brand-400 transition-all placeholder:text-surface-400"
            />
          </div>

          {/* Quality Filter */}
          <div>
            <select
              value={filters.quality}
              onChange={(e) => setFilters({ quality: e.target.value as QualityRating | 'all' })}
              className="select-field"
            >
              <option value="all">All Quality Ratings</option>
              <option value="satisfactory">Satisfactory</option>
              <option value="needs_improvement">Needs Improvement</option>
              <option value="unsatisfactory">Unsatisfactory</option>
            </select>
          </div>

          {/* Status Filter */}
          <div>
            <select
              value={filters.status}
              onChange={(e) => setFilters({ status: e.target.value as any })}
              className="select-field"
            >
              <option value="all">All Statuses</option>
              <option value="processing">Processing</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
            </select>
          </div>
        </div>
      </div>

      {/* Documents Table */}
      <div className="glass-card animate-slide-up overflow-hidden">
        <div className="px-5 pt-5 pb-3">
          <h3 className="section-title">Document History</h3>
          <p className="section-subtitle">{sortedDocs.length} document{sortedDocs.length !== 1 ? 's' : ''} found</p>
        </div>

        {sortedDocs.length === 0 ? (
          <div className="flex items-center justify-center py-16 text-surface-400">
            <div className="text-center">
              <FileText size={40} className="mx-auto mb-3 text-surface-300" />
              <p className="text-sm font-medium">No documents match your filters</p>
              <p className="text-xs mt-1">Try adjusting your search or filter criteria</p>
            </div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="table-header">
                  <th className="table-cell">Document Name</th>
                  <th className="table-cell">Document ID</th>
                  <th className="table-cell">Doc Type</th>
                  <th className="table-cell">File Size</th>
                  <th className="table-cell">Date</th>
                  <th className="table-cell">Quality Rating</th>
                  <th className="table-cell text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedDocs.map((doc) => (
                  <tr
                    key={doc.id}
                    onClick={() => navigateToDocument(doc.id)}
                    className="table-row cursor-pointer"
                  >
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-lg bg-surface-100 flex items-center justify-center flex-shrink-0">
                          <FileText size={14} className="text-surface-500" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-surface-800">{doc.fileName}</p>
                          <p className="text-[11px] text-surface-400">{doc.templateVersion}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-3">
                      <span className="text-xs font-mono text-brand-400">{doc.docName}</span>
                    </td>
                    <td className="px-5 py-3">
                      <span className="text-xs font-medium text-surface-600 bg-surface-100 px-2 py-1 rounded-lg">{doc.fileType}</span>
                    </td>
                    <td className="px-5 py-3">
                      <span className="text-xs text-surface-500">{doc.fileSize}</span>
                    </td>
                    <td className="px-5 py-3">
                      <span className="text-xs text-surface-500">
                        {new Date(doc.uploadDate).toLocaleDateString()}
                      </span>
                    </td>
                    <td className="px-5 py-3">
                      {doc.qualityRating ? (
                        <QualityBadge quality={doc.qualityRating as any} />
                      ) : (
                        <DocumentStatusBadge status={doc.status as any} />
                      )}
                    </td>
                    <td className="px-5 py-3 text-right">
                      <button
                        onClick={(e) => { e.stopPropagation(); navigateToDocument(doc.id); }}
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-brand-400 bg-brand-50 rounded-lg hover:bg-brand-100 transition-colors"
                      >
                        <Eye size={12} /> View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Quality Rating Legend */}
      <div className="glass-card p-6 animate-slide-up">
        <h3 className="section-title mb-4">Quality Rating Legend</h3>
        <div className="grid grid-cols-3 gap-4">
          {(Object.entries(qualityRatingDescriptions) as [string, { label: string; description: string; color: string }][]).map(([key, rating]) => {
            const colorMap: Record<string, string> = {
              green: 'border-l-emerald-400',
              amber: 'border-l-amber-400',
              red: 'border-l-red-400',
            };

            return (
              <div
                key={key}
                className={`bg-surface-50 rounded-xl p-4 border-l-4 ${colorMap[rating.color] || 'border-l-surface-300'}`}
              >
                <p className="text-sm font-semibold text-surface-800 mb-1">{rating.label}</p>
                <p className="text-xs text-surface-500 leading-relaxed">{rating.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
